/**
 * Location Search — frontend map, autocomplete, and results.
 *
 * @package Vertaview
 */

( function () {
	'use strict';

	const DEFAULT_PIN_COLORS = {
		clinic: '#D26A2B',
		user: '#4F082B',
	};
	const AUSTRALIA_CENTER = { lat: -25.2744, lng: 133.7751 };
	const DEFAULT_ZOOM = 4;

	/**
	 * Parse block config from the wrapper data attribute.
	 *
	 * @param {HTMLElement} wrap Block wrapper.
	 * @return {Object|null}
	 */
	function parseConfig( wrap ) {
		const raw = wrap.getAttribute( 'data-location-search-config' );

		if ( ! raw ) {
			return null;
		}

		try {
			return JSON.parse( raw );
		} catch ( error ) {
			return null;
		}
	}

	/**
	 * Wait until the Google Maps API is available.
	 *
	 * @return {Promise<void>}
	 */
	function whenGoogleMapsReady() {
		return new Promise( ( resolve, reject ) => {
			if ( window.google?.maps?.Map ) {
				resolve();
				return;
			}

			let attempts = 0;
			const maxAttempts = 200;

			const timer = window.setInterval( () => {
				attempts += 1;

				if ( window.google?.maps?.Map ) {
					window.clearInterval( timer );
					resolve();
					return;
				}

				if ( attempts >= maxAttempts ) {
					window.clearInterval( timer );
					reject( new Error( 'Google Maps failed to load.' ) );
				}
			}, 50 );
		} );
	}

	/**
	 * Create a circular marker icon config (Support centre locations).
	 *
	 * @param {string} color  Fill colour.
	 * @param {number} scale  Marker scale.
	 * @return {Object}
	 */
	function circleIcon( color, scale ) {
		return {
			path: window.google.maps.SymbolPath.CIRCLE,
			scale,
			fillColor: color,
			fillOpacity: 1,
			strokeColor: '#FFFFFF',
			strokeWeight: 2,
		};
	}

	/**
	 * Create a map pin icon for the searched user location.
	 *
	 * @param {string} color Fill colour (primary).
	 * @return {Object}
	 */
	function userPinIcon( color ) {
		const svg = [
			'<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 24 24">',
			`<path fill="${ color }" stroke="#FFFFFF" stroke-width="1" d="M12 2C7.58 2 4 5.58 4 10c0 5.25 8 14 8 14s8-8.75 8-14c0-4.42-3.58-8-8-8z"/>`,
			'<circle fill="#FFFFFF" cx="12" cy="10" r="3"/>',
			'</svg>',
		].join( '' );

		return {
			url: `data:image/svg+xml;charset=UTF-8,${ encodeURIComponent( svg ) }`,
			scaledSize: new window.google.maps.Size( 36, 44 ),
			anchor: new window.google.maps.Point( 18, 44 ),
		};
	}

	/**
	 * Initialise a single location search block.
	 *
	 * @param {HTMLElement} wrap Block wrapper.
	 */
	async function initBlock( wrap ) {
		const config = parseConfig( wrap );

		if ( ! config?.apiKey ) {
			return;
		}

		const mapEl = wrap.querySelector( '[data-map]' );
		const formEl = wrap.querySelector( '[data-search-form]' );
		const inputEl = wrap.querySelector( '[data-search-input]' );
		const latEl = wrap.querySelector( '[data-search-lat]' );
		const lngEl = wrap.querySelector( '[data-search-lng]' );
		const searchErrorEl = wrap.querySelector( '[data-search-error]' );
		const serviceCardsEl = wrap.querySelector( '[data-service-cards]' );
		const clinicSectionEl = wrap.querySelector( '[data-clinic-section]' );
		const clinicHeadingEl = wrap.querySelector( '[data-clinic-heading]' );
		const clinicGridEl = wrap.querySelector( '[data-clinic-grid]' );
		const clinicEmptyEl = wrap.querySelector( '[data-clinic-empty]' );
		const telehealthOnlyEl = wrap.querySelector( '[data-telehealth-only]' );
		const notInAreaEl = wrap.querySelector( '[data-not-in-area]' );
		const clearEl = wrap.querySelector( '[data-search-clear]' );
		const errorEl = wrap.querySelector( '[data-error]' );

		if ( ! mapEl || ! formEl || ! inputEl || ! latEl || ! lngEl ) {
			return;
		}

		try {
			await whenGoogleMapsReady();
		} catch ( error ) {
			return;
		}

		const state = {
			map: null,
			polygons: [],
			clinicMarkers: new Map(),
			userMarker: null,
			autocomplete: null,
			searched: false,
		};

		const mapOptions = {
			center: AUSTRALIA_CENTER,
			zoom: DEFAULT_ZOOM,
			disableDefaultUI: true,
		};

		if ( config.mapId ) {
			mapOptions.mapId = config.mapId;
		}

		state.map = new window.google.maps.Map( mapEl, mapOptions );

		const pinColors = { ...DEFAULT_PIN_COLORS, ...config.pinColors };

		await drawPolygons( state, config.polygons || [] );
		placeClinicMarkers( state, config.clinics || [], pinColors );

		initAutocomplete( state, inputEl, latEl, lngEl, searchErrorEl );
		bindSearchForm(
			wrap,
			state,
			formEl,
			inputEl,
			latEl,
			lngEl,
			searchErrorEl,
			clearEl,
			{
				serviceCardsEl,
				clinicSectionEl,
				clinicHeadingEl,
				clinicGridEl,
				clinicEmptyEl,
				telehealthOnlyEl,
				notInAreaEl,
				errorEl,
			},
			config,
			pinColors
		);
		bindClearSearch(
			wrap,
			state,
			clearEl,
			inputEl,
			latEl,
			lngEl,
			searchErrorEl,
			{
				serviceCardsEl,
				clinicSectionEl,
				clinicHeadingEl,
				clinicGridEl,
				clinicEmptyEl,
				telehealthOnlyEl,
				notInAreaEl,
				errorEl,
			},
			config,
			pinColors
		);
	}

	/**
	 * Load polygon rings from a static geo JSON asset.
	 *
	 * @param {string} geoUrl Asset URL.
	 * @return {Promise<Object[][]>}
	 */
	async function loadGeoPolygons( geoUrl ) {
		const response = await window.fetch( geoUrl );

		if ( ! response.ok ) {
			return [];
		}

		const data = await response.json();

		if ( data?.multi && Array.isArray( data.polygons ) ) {
			return data.polygons.filter( ( ring ) => Array.isArray( ring ) && ring.length >= 3 );
		}

		if ( Array.isArray( data ) && data.length >= 3 ) {
			return [ data ];
		}

		return [];
	}

	/**
	 * Draw a single polygon ring on the map.
	 *
	 * @param {Object}   state   Block state.
	 * @param {Object[]} coords  Ring coordinates.
	 * @param {string}   color   Polygon colour.
	 */
	function drawPolygonRing( state, coords, color ) {
		const overlay = new window.google.maps.Polygon( {
			paths: coords,
			strokeWeight: 1,
			strokeOpacity: 0.8,
			strokeColor: color,
			fillColor: color,
			fillOpacity: 0.3,
			map: state.map,
		} );

		state.polygons.push( overlay );
	}

	/**
	 * Draw coverage polygons on the map.
	 *
	 * @param {Object}   state    Block state.
	 * @param {Object[]} polygons Polygon config.
	 */
	async function drawPolygons( state, polygons ) {
		for ( const polygon of polygons ) {
			if ( polygon.multi && polygon.geoUrl ) {
				const rings = await loadGeoPolygons( polygon.geoUrl );

				rings.forEach( ( ring ) => {
					drawPolygonRing( state, ring, polygon.color );
				} );

				continue;
			}

			if ( polygon.polygons?.length ) {
				polygon.polygons.forEach( ( ring ) => {
					if ( ring?.length ) {
						drawPolygonRing( state, ring, polygon.color );
					}
				} );

				continue;
			}

			if ( polygon.coords?.length ) {
				drawPolygonRing( state, polygon.coords, polygon.color );
			}
		}
	}

	/**
	 * Place clinic markers and store references by clinic ID.
	 *
	 * @param {Object}   state   Block state.
	 * @param {Object[]} clinics Clinic config.
	 */
	function placeClinicMarkers( state, clinics, pinColors ) {
		clinics.forEach( ( clinic ) => {
			const marker = new window.google.maps.Marker( {
				position: { lat: clinic.lat, lng: clinic.lng },
				map: state.map,
				title: clinic.title,
				icon: circleIcon( pinColors.clinic, 7 ),
			} );

			state.clinicMarkers.set( clinic.id, marker );
		} );
	}

	/**
	 * Attach Places Autocomplete to the search input.
	 *
	 * @param {Object}            state          Block state.
	 * @param {HTMLInputElement}  inputEl        Search input.
	 * @param {HTMLInputElement}  latEl          Hidden latitude input.
	 * @param {HTMLInputElement}  lngEl          Hidden longitude input.
	 * @param {HTMLElement|null}  searchErrorEl  Inline validation message.
	 */
	function initAutocomplete( state, inputEl, latEl, lngEl, searchErrorEl ) {
		state.autocomplete = new window.google.maps.places.Autocomplete( inputEl, {
			componentRestrictions: { country: 'au' },
			types: [ '(regions)' ],
			fields: [ 'geometry' ],
		} );

		state.autocomplete.addListener( 'place_changed', () => {
			const place = state.autocomplete.getPlace();

			if ( ! place?.geometry?.location ) {
				latEl.value = '';
				lngEl.value = '';
				return;
			}

			latEl.value = String( place.geometry.location.lat() );
			lngEl.value = String( place.geometry.location.lng() );

			if ( searchErrorEl ) {
				searchErrorEl.hidden = true;
				searchErrorEl.textContent = '';
			}
		} );
	}

	/**
	 * Bind search form submission.
	 *
	 * @param {HTMLElement}      wrap           Block wrapper.
	 * @param {Object}           state          Block state.
	 * @param {HTMLFormElement}  formEl         Search form.
	 * @param {HTMLInputElement} latEl          Hidden latitude input.
	 * @param {HTMLInputElement} lngEl          Hidden longitude input.
	 * @param {HTMLElement|null} searchErrorEl  Inline validation message.
	 * @param {Object}           ui             DOM references for results UI.
	 * @param {Object}           config         Block config.
	 */
	function bindSearchForm( wrap, state, formEl, inputEl, latEl, lngEl, searchErrorEl, clearEl, ui, config, pinColors ) {
		formEl.addEventListener( 'submit', async ( event ) => {
			event.preventDefault();

			if ( ui.errorEl ) {
				ui.errorEl.hidden = true;
			}

			const lat = parseFloat( latEl.value );
			const lng = parseFloat( lngEl.value );

			if ( Number.isNaN( lat ) || Number.isNaN( lng ) ) {
				if ( searchErrorEl ) {
					searchErrorEl.hidden = false;
					searchErrorEl.textContent = 'Please select a suburb or postcode from the suggestions.';
				}
				return;
			}

			if ( searchErrorEl ) {
				searchErrorEl.hidden = true;
				searchErrorEl.textContent = '';
			}

			wrap.classList.add( 'is-loading' );

			try {
				const data = await fetchLocations( config, lat, lng );
				state.searched = true;
				updateMap( state, data, lat, lng, pinColors );
				renderResults( ui, data, config );

				if ( clearEl ) {
					clearEl.hidden = false;
				}
			} catch ( error ) {
				if ( ui.errorEl ) {
					ui.errorEl.hidden = false;
				}
			} finally {
				wrap.classList.remove( 'is-loading' );
			}
		} );
	}

	/**
	 * Bind clear search control.
	 *
	 * @param {HTMLElement}           wrap           Block wrapper.
	 * @param {Object}                state          Block state.
	 * @param {HTMLButtonElement|null} clearEl       Clear button.
	 * @param {HTMLInputElement}      inputEl        Search input.
	 * @param {HTMLInputElement}      latEl          Hidden latitude input.
	 * @param {HTMLInputElement}      lngEl          Hidden longitude input.
	 * @param {HTMLElement|null}      searchErrorEl  Inline validation message.
	 * @param {Object}                ui             DOM references for results UI.
	 * @param {Object}                config         Block config.
	 * @param {Object}                pinColors      Map pin colours.
	 */
	function bindClearSearch( wrap, state, clearEl, inputEl, latEl, lngEl, searchErrorEl, ui, config, pinColors ) {
		if ( ! clearEl ) {
			return;
		}

		clearEl.addEventListener( 'click', () => {
			clearSearchResults( wrap, state, clearEl, inputEl, latEl, lngEl, searchErrorEl, ui, config, pinColors );
		} );
	}

	/**
	 * Reset search results to the initial page state.
	 *
	 * @param {HTMLElement}           wrap           Block wrapper.
	 * @param {Object}                state          Block state.
	 * @param {HTMLButtonElement|null} clearEl       Clear button.
	 * @param {HTMLInputElement}      inputEl        Search input.
	 * @param {HTMLInputElement}      latEl          Hidden latitude input.
	 * @param {HTMLInputElement}      lngEl          Hidden longitude input.
	 * @param {HTMLElement|null}      searchErrorEl  Inline validation message.
	 * @param {Object}                ui             DOM references for results UI.
	 * @param {Object}                config         Block config.
	 * @param {Object}                pinColors      Map pin colours.
	 */
	function clearSearchResults( wrap, state, clearEl, inputEl, latEl, lngEl, searchErrorEl, ui, config, pinColors ) {
		state.searched = false;

		inputEl.value = '';
		latEl.value = '';
		lngEl.value = '';

		if ( searchErrorEl ) {
			searchErrorEl.hidden = true;
			searchErrorEl.textContent = '';
		}

		if ( ui.errorEl ) {
			ui.errorEl.hidden = true;
		}

		if ( state.userMarker ) {
			state.userMarker.setMap( null );
			state.userMarker = null;
		}

		state.clinicMarkers.forEach( ( marker ) => {
			marker.setIcon( circleIcon( pinColors.clinic, 7 ) );
		} );

		state.map.setCenter( AUSTRALIA_CENTER );
		state.map.setZoom( DEFAULT_ZOOM );

		resetResultsUI( ui, config );

		if ( clearEl ) {
			clearEl.hidden = true;
		}
	}

	/**
	 * Restore service cards, clinic grid, and messages to the initial load state.
	 *
	 * @param {Object} ui     DOM references.
	 * @param {Object} config Block config.
	 */
	function resetResultsUI( ui, config ) {
		const cardEls = {
			inPerson: ui.serviceCardsEl?.querySelector( '[data-service-card="in-person"]' ),
			telehealth: ui.serviceCardsEl?.querySelector( '[data-service-card="telehealth"]' ),
			clinic: ui.serviceCardsEl?.querySelector( '[data-service-card="clinic"]' ),
		};

		Object.values( cardEls ).forEach( ( card ) => {
			if ( card ) {
				card.hidden = false;
			}
		} );

		if ( ui.telehealthOnlyEl ) {
			ui.telehealthOnlyEl.hidden = true;
		}

		if ( ui.notInAreaEl ) {
			ui.notInAreaEl.hidden = true;
		}

		if ( ui.clinicEmptyEl ) {
			ui.clinicEmptyEl.hidden = true;
		}

		if ( ui.clinicSectionEl ) {
			ui.clinicSectionEl.hidden = false;
		}

		if ( ui.clinicHeadingEl ) {
			ui.clinicHeadingEl.hidden = false;
			ui.clinicHeadingEl.textContent = config.i18n?.allHeading || 'All support centre locations.';
		}

		if ( ui.clinicGridEl ) {
			ui.clinicGridEl.hidden = false;
			ui.clinicGridEl.innerHTML = buildClinicGroupsHtml( config.clinics || [], config );
		}
	}

	/**
	 * Fetch location results from the REST API.
	 *
	 * @param {Object} config Block config.
	 * @param {number} lat    Latitude.
	 * @param {number} lng    Longitude.
	 * @return {Promise<Object>}
	 */
	async function fetchLocations( config, lat, lng ) {
		const url = new URL(
			`${ config.restNamespace }/locations`,
			config.restRoot
		);

		url.searchParams.set( 'lat', String( lat ) );
		url.searchParams.set( 'lng', String( lng ) );

		const response = await window.fetch( url.toString() );

		if ( ! response.ok ) {
			throw new Error( 'Location search request failed.' );
		}

		return response.json();
	}

	/**
	 * Update map markers after a successful search.
	 *
	 * @param {Object} state Block state.
	 * @param {Object} data  REST response.
	 * @param {number} lat    User latitude.
	 * @param {number} lng    User longitude.
	 */
	function updateMap( state, data, lat, lng, pinColors ) {
		state.clinicMarkers.forEach( ( marker ) => {
			marker.setIcon( circleIcon( pinColors.clinic, 7 ) );
		} );

		if ( state.userMarker ) {
			state.userMarker.setMap( null );
		}

		state.userMarker = new window.google.maps.Marker( {
			position: { lat, lng },
			map: state.map,
			title: 'Your location',
			icon: userPinIcon( pinColors.user ),
			zIndex: 999,
		} );

		const bounds = new window.google.maps.LatLngBounds();
		bounds.extend( { lat, lng } );

		const highlighted = data.clinics || [];

		highlighted.forEach( ( clinic ) => {
			const marker = state.clinicMarkers.get( clinic.id );

			if ( marker ) {
				marker.setIcon( circleIcon( pinColors.clinic, 10 ) );
				bounds.extend( marker.getPosition() );
			}
		} );

		if ( highlighted.length ) {
			state.map.fitBounds( bounds );
		} else {
			state.map.setCenter( { lat, lng } );
			state.map.setZoom( 8 );
		}
	}

	/**
	 * Update service cards and clinic grid from REST data.
	 *
	 * @param {Object} ui     DOM references.
	 * @param {Object} data   REST response.
	 * @param {Object} config Block config.
	 */
	function renderResults( ui, data, config ) {
		const inPerson = !! data.in_person;
		const telehealth = !! data.telehealth;
		const clinics = data.clinics || [];

		const cardEls = {
			inPerson: ui.serviceCardsEl?.querySelector( '[data-service-card="in-person"]' ),
			telehealth: ui.serviceCardsEl?.querySelector( '[data-service-card="telehealth"]' ),
			clinic: ui.serviceCardsEl?.querySelector( '[data-service-card="clinic"]' ),
		};

		if ( ui.telehealthOnlyEl ) {
			ui.telehealthOnlyEl.hidden = true;
		}

		if ( ui.notInAreaEl ) {
			ui.notInAreaEl.hidden = true;
		}

		if ( ui.clinicEmptyEl ) {
			ui.clinicEmptyEl.hidden = true;
		}

		if ( ! inPerson && telehealth ) {
			if ( cardEls.inPerson ) {
				cardEls.inPerson.hidden = true;
			}

			if ( cardEls.telehealth ) {
				cardEls.telehealth.hidden = false;
			}

			if ( cardEls.clinic ) {
				cardEls.clinic.hidden = true;
			}

			if ( ui.telehealthOnlyEl ) {
				ui.telehealthOnlyEl.hidden = false;
			}

			if ( ui.clinicSectionEl ) {
				ui.clinicSectionEl.hidden = true;
			}

			return;
		}

		if ( ! inPerson && ! telehealth ) {
			Object.values( cardEls ).forEach( ( card ) => {
				if ( card ) {
					card.hidden = true;
				}
			} );

			if ( ui.notInAreaEl ) {
				ui.notInAreaEl.hidden = false;
			}

			if ( ui.clinicSectionEl ) {
				ui.clinicSectionEl.hidden = true;
			}

			return;
		}

		if ( cardEls.inPerson ) {
			cardEls.inPerson.hidden = false;
		}

		if ( cardEls.telehealth ) {
			cardEls.telehealth.hidden = ! telehealth;
		}

		if ( cardEls.clinic ) {
			cardEls.clinic.hidden = ! clinics.length;
		}

		if ( clinics.length ) {
			if ( ui.clinicSectionEl ) {
				ui.clinicSectionEl.hidden = false;
			}

			if ( ui.clinicHeadingEl ) {
				ui.clinicHeadingEl.hidden = false;
				ui.clinicHeadingEl.textContent = config.i18n?.nearestHeading || 'Nearest Support centre locations.';
			}

			if ( ui.clinicGridEl ) {
				ui.clinicGridEl.hidden = false;
				ui.clinicGridEl.innerHTML = buildClinicGroupsHtml( clinics, config );
			}
		} else {
			if ( ui.clinicSectionEl ) {
				ui.clinicSectionEl.hidden = true;
			}

			if ( ui.clinicGridEl ) {
				ui.clinicGridEl.hidden = true;
				ui.clinicGridEl.innerHTML = '';
			}

			if ( ui.clinicEmptyEl ) {
				ui.clinicEmptyEl.hidden = false;

				const headingEl = ui.clinicEmptyEl.querySelector( '.location-search__message-title' );
				const copyEls = ui.clinicEmptyEl.querySelectorAll( 'p' );

				if ( headingEl ) {
					headingEl.textContent = config.i18n?.noClinicHeading || 'There isn\'t a clinic near you';
				}

				if ( copyEls[ 0 ] ) {
					copyEls[ 0 ].textContent = config.i18n?.noClinicCopy1
						|| 'That\'s okay. In fact, many of our practitioners prefer to work in the places where life actually happens — at home, at school, at work, or in the community.';
				}

				if ( copyEls[ 1 ] ) {
					copyEls[ 1 ].textContent = config.i18n?.noClinicCopy2
						|| 'In-person support is available in your area, with Telehealth video appointments often used between visits, particularly in regional locations. Get in touch and we\'ll talk through the approach that\'s likely to work best for you.';
				}
			}
		}
	}

	/**
	 * Group clinics by state code.
	 *
	 * @param {Object[]} clinics Clinic arrays.
	 * @return {Object<string, Object[]>}
	 */
	function groupClinicsByState( clinics ) {
		const grouped = {};

		clinics.forEach( ( clinic ) => {
			const state = clinic.state || 'OTHER';

			if ( ! grouped[ state ] ) {
				grouped[ state ] = [];
			}

			grouped[ state ].push( clinic );
		} );

		return grouped;
	}

	/**
	 * Escape a string for use in an HTML attribute value.
	 *
	 * @param {string} value Raw value.
	 * @return {string}
	 */
	function escapeAttr( value ) {
		return String( value )
			.replace( /&/g, '&amp;' )
			.replace( /"/g, '&quot;' )
			.replace( /</g, '&lt;' );
	}

	/**
	 * Accessible label for clinic directions links.
	 *
	 * @param {string} title Clinic name.
	 * @param {Object} i18n   Block i18n strings.
	 * @return {string}
	 */
	function getDirectionsAriaLabel( title, i18n = {} ) {
		const template = i18n.directionsLabel || 'Get directions to %s';

		return template.replace( '%s', title );
	}

	/**
	 * Build clinic card HTML for JS updates.
	 *
	 * @param {Object} clinic Clinic data.
	 * @param {Object} icons  Icon markup from block config.
	 * @param {Object} i18n   Block i18n strings.
	 * @return {string}
	 */
	function buildClinicCardHtml( clinic, icons = {}, i18n = {} ) {
		const phoneLink = clinic.phone
			? `<p><a href="tel:${ clinic.phone.replace( /\s+/g, '' ) }">${ clinic.phone }</a></p>`
			: '';
		const emailLink = clinic.email
			? `<p><a href="mailto:${ clinic.email }">${ clinic.email }</a></p>`
			: '';
		const address = clinic.address
			? `<p class="location-search__clinic-card-address">${ clinic.address }</p>`
			: '';
		const locationIcon = icons.clinicLocation || '';
		const directionsArrow = icons.directionsArrow
			? `<span aria-hidden="true">${ icons.directionsArrow }</span>`
			: '<span aria-hidden="true">→</span>';
		const directionsAriaLabel = escapeAttr( getDirectionsAriaLabel( clinic.title, i18n ) );
		const directions = clinic.directionsUrl
			? `<a class="location-search__clinic-card-link" href="${ clinic.directionsUrl }" target="_blank" rel="noopener noreferrer" aria-label="${ directionsAriaLabel }">Get directions${ directionsArrow }</a>`
			: '';

		return `
			<article class="location-search__clinic-card" data-clinic-id="${ clinic.id }">
				<div class="location-search__clinic-card-body">
					<div class="location-search__clinic-card-header">
						<span class="location-search__clinic-card-icon" aria-hidden="true">${ locationIcon }</span>
						<h3 class="location-search__clinic-card-title">${ clinic.title }</h3>
					</div>
					<div class="location-search__clinic-card-details">
						${ address }
						${ phoneLink }
						${ emailLink }
					</div>
					${ directions }
				</div>
			</article>
		`;
	}

	/**
	 * Build grouped clinic markup.
	 *
	 * @param {Object[]} clinics Clinic arrays.
	 * @param {Object}   config Block config (unused today, reserved for i18n).
	 * @return {string}
	 */
	function buildClinicGroupsHtml( clinics, config ) {
		const grouped = groupClinicsByState( clinics );
		const stateLabels = config.stateLabels || {};
		const icons = config.icons || {};
		const i18n = config.i18n || {};

		return Object.entries( grouped )
			.map( ( [ stateCode, stateClinics ] ) => {
				const label = stateLabels[ stateCode ] || stateCode;
				const cards = stateClinics
					.map( ( clinic ) => buildClinicCardHtml( clinic, icons, i18n ) )
					.join( '' );

				return `
					<div class="location-search__clinic-group" data-state="${ stateCode }">
						<p class="location-search__clinic-group-label">${ label }</p>
						<div class="location-search__clinic-group-grid">${ cards }</div>
					</div>
				`;
			} )
			.join( '' );
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document
			.querySelectorAll( '[data-location-search]' )
			.forEach( ( wrap ) => initBlock( wrap ) );
	} );
} )();
